import type { ServerEvents, ClientEvents } from '@bangos/shared';

export type ServerEventName = keyof ServerEvents;
export type ClientEventName = keyof ClientEvents;

/** Parameter types for each server event (extracted from the callback signature). */
type ServerEventPayload<E extends ServerEventName> = ServerEvents[E] extends (...args: infer A) => void ? A[0] : never;

const LOCALSTORAGE_PLAYER_ID_KEY = 'bangos_player_id';
const LOCALSTORAGE_ROOM_CODE_KEY = 'bangos_room_code';

/** Generate a unique connection ID (crypto.randomUUID with fallback). */
function generateConnectionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'conn-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
}

/**
 * Client-side WebSocket connection manager.
 * Uses native WebSocket with reconnection, heartbeat, and typed event passing.
 * Supports player identity persistence for reconnection across page reloads.
 *
 * For Cloudflare Workers deployment:
 * - Rooms are created via REST POST to /api/create
 * - WebSocket connections target /room/<roomCode> per room
 * - First message after WS open must be `join_room`
 */
export class SocketManager {
  private ws: WebSocket | null = null;
  private serverUrl: string = '';
  private currentRoomCode: string | null = null;
  private connectionId: string | null = null;
  private readonly createWebSocket: (url: string) => WebSocket;

  // Reconnection state
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;

  // Pending join payload sent on next WS open
  private pendingJoinPayload: { roomCode: string; displayName: string; asSpectator: boolean; playerId: string } | null = null;

  // Heartbeat state
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPongReceived = 0;

  // Event registry: maps event names to sets of handler functions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handlers = new Map<string, Set<(...args: any[]) => void>>();

  /** Handler called when a game_state_sync event is received. */
  private gameStateHandler: ((gameState: unknown) => void) | null = null;
  /** Handler called when a game_state_diff event is received. */
  private gameStateDiffHandler: ((diff: unknown) => void) | null = null;
  /** Callback for connection state changes. */
  private connectionStateHandler: ((connected: boolean) => void) | null = null;

  /**
   * @param wsFactory - Optional WebSocket factory for testing. Defaults to the global WebSocket.
   */
  constructor(wsFactory?: (url: string) => WebSocket) {
    this.createWebSocket = wsFactory ?? ((url: string) => new WebSocket(url));
  }

  /**
   * Connect to a specific room's WebSocket endpoint.
   * For Cloudflare Workers, the WebSocket URL is: {baseUrl}/room/{roomCode}
   * After connecting, sends `join_room` with the provided payload.
   *
   * @param roomCode - The room code to connect to. If omitted, uses stored identity.
   * @param joinPayload - Payload for the `join_room` message sent on open.
   * @param serverUrl - Optional base server URL override.
   */
  connectToRoom(
    roomCode?: string,
    joinPayload?: { displayName?: string; asSpectator?: boolean; playerId?: string },
    serverUrl?: string,
  ): void {
    // Determine the room code to use
    const code = roomCode || this.getStoredIdentity()?.roomCode || null;
    if (!code) {
      console.warn('[socket-manager] connectToRoom called without roomCode and no stored identity');
      return;
    }

    // If already connected to this same room, don't reconnect.
    // Just update the pending payload if a new one was provided.
    if (this.connected && this.currentRoomCode === code) {
      if (joinPayload) {
        this.pendingJoinPayload = {
          roomCode: code,
          displayName: joinPayload.displayName || '',
          asSpectator: joinPayload.asSpectator ?? false,
          playerId: joinPayload.playerId || '',
        };
        // Send the join payload immediately if already connected
        this.emit('join_room', this.pendingJoinPayload);
        this.pendingJoinPayload = null;
      }
      return;
    }

    this.currentRoomCode = code;

    // Store the join payload for sending on open
    if (joinPayload) {
      this.pendingJoinPayload = {
        roomCode: code,
        displayName: joinPayload.displayName || '',
        asSpectator: joinPayload.asSpectator ?? false,
        playerId: joinPayload.playerId || '',
      };
    }

    const baseUrl = serverUrl || this.resolveBaseUrl();
    const wsUrl = `${baseUrl}/room/${code}`;
    this.connect(wsUrl);
  }

  /**
   * Connect to a WebSocket URL.
   * @param wsUrl - The full WebSocket URL to connect to.
   */
  connect(wsUrl?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.intentionalClose = false;
    const targetUrl = wsUrl || this.serverUrl;
    this.serverUrl = targetUrl;

    try {
      this.ws = this.createWebSocket(targetUrl);
    } catch (err) {
      console.error('[socket-manager] Failed to create WebSocket:', err);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.connectionId = generateConnectionId();
      this.reconnectAttempts = 0;
      console.log(`[socket-manager] connected: ${this.connectionId} to room ${this.currentRoomCode}`);
      this.connectionStateHandler?.(true);

      // Start heartbeat
      this.startHeartbeat();

      // After connect (including reconnect), attempt to re-identify the player
      // Priority: pending join payload > stored identity
      if (this.pendingJoinPayload) {
        console.log('[socket-manager] sending join_room with pending payload', this.pendingJoinPayload);
        this.emit('join_room', this.pendingJoinPayload);
        this.pendingJoinPayload = null;
      } else {
        const identity = this.getStoredIdentity();
        if (identity && this.currentRoomCode) {
          console.log('[socket-manager] re-joining room with stored identity', identity);
          this.emit('join_room', {
            playerId: identity.playerId,
            roomCode: this.currentRoomCode,
            displayName: '', // Server will use stored displayName for reconnects
          });
        }
      }
    };

    this.ws.onmessage = (event: MessageEvent) => {
      // Reset pong timer on any message (both pong and regular messages indicate liveness)
      this.lastPongReceived = Date.now();
      this.resetPongTimeout();

      try {
        const envelope = JSON.parse(event.data as string);
        const { type, payload } = envelope;

        // Handle pong specifically
        if (type === 'pong') return;

        // Log game state events
        if (type === 'game_state_sync') {
          console.log('[socket-manager] received game_state_sync');
          this.gameStateHandler?.(payload.gameState);
          return;
        }
        if (type === 'game_state_diff') {
          console.log('[socket-manager] received game_state_diff');
          this.gameStateDiffHandler?.(payload.diff);
          return;
        }

        // Dispatch to registered handlers
        const eventHandlers = this.handlers.get(type);
        if (eventHandlers) {
          for (const handler of eventHandlers) {
            try {
              handler(payload);
            } catch (err) {
              console.error(`[socket-manager] Error in handler for "${type}":`, err);
            }
          }
        }
      } catch (err) {
        console.error('[socket-manager] Failed to parse message:', err);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.log(`[socket-manager] disconnected: code=${event.code} reason=${event.reason}`);
      this.stopHeartbeat();
      this.connectionStateHandler?.(false);

      // Don't reconnect on intentional close (code 1000)
      if (event.code === 1000 || this.intentionalClose) {
        this.ws = null;
        this.connectionId = null;
        return;
      }

      // Schedule reconnection with exponential backoff
      this.ws = null;
      this.connectionId = null;
      this.scheduleReconnect();
    };

    this.ws.onerror = (event: Event) => {
      console.error('[socket-manager] WebSocket error:', event);
      // onclose will fire after onerror, handling reconnection there
    };
  }

  /**
   * Create a new room via the REST API.
   * POST /api/create → returns { roomCode }.
   *
   * @param serverUrl - Optional base HTTP server URL override.
   * @returns The created room's code.
   */
  async createRoomViaRest(serverUrl?: string): Promise<{ roomCode: string }> {
    const baseUrl = (serverUrl || this.resolveBaseUrl()).replace(/^ws(s?):\/\//, 'http$1://');
    const response = await fetch(`${baseUrl}/api/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create room: ${response.status} ${errorText}`);
    }
    const data = await response.json() as { roomCode: string };
    return data;
  }

  /**
   * Disconnect from the WebSocket server.
   */
  disconnect(): void {
    this.intentionalClose = true;
    this.stopHeartbeat();
    this.clearReconnectTimer();
    if (this.ws) {
      this.ws.close(1000, 'Intentional disconnect');
      this.ws = null;
    }
    this.connectionId = null;
    this.currentRoomCode = null;
  }

  /**
   * Store the player's identity in localStorage for reconnection.
   */
  storeIdentity(playerId: string, roomCode: string): void {
    try {
      localStorage.setItem(LOCALSTORAGE_PLAYER_ID_KEY, playerId);
      localStorage.setItem(LOCALSTORAGE_ROOM_CODE_KEY, roomCode);
    } catch {
      // localStorage may not be available (e.g., SSR, privacy mode)
      console.warn('[socket-manager] unable to store identity in localStorage');
    }
  }

  /**
   * Retrieve the stored player identity from localStorage.
   */
  getStoredIdentity(): { playerId: string; roomCode: string } | null {
    try {
      const playerId = localStorage.getItem(LOCALSTORAGE_PLAYER_ID_KEY);
      const roomCode = localStorage.getItem(LOCALSTORAGE_ROOM_CODE_KEY);
      if (playerId && roomCode) {
        return { playerId, roomCode };
      }
    } catch {
      // localStorage may not be available
    }
    return null;
  }

  /**
   * Clear the stored player identity from localStorage.
   */
  clearIdentity(): void {
    try {
      localStorage.removeItem(LOCALSTORAGE_PLAYER_ID_KEY);
      localStorage.removeItem(LOCALSTORAGE_ROOM_CODE_KEY);
    } catch {
      // ignore
    }
  }

  /**
   * Register a handler to be called when game_state_sync is received.
   */
  onGameStateSync(handler: (gameState: unknown) => void): void {
    this.gameStateHandler = handler;
  }

  /**
   * Register a handler to be called when game_state_diff is received.
   */
  onGameStateDiff(handler: (diff: unknown) => void): void {
    this.gameStateDiffHandler = handler;
  }

  /**
   * Register a handler to be called when the socket connection state changes.
   */
  onConnectionStateChange(handler: (connected: boolean) => void): void {
    this.connectionStateHandler = handler;
  }

  /**
   * Listen for a server event.
   */
  on<E extends ServerEventName>(
    event: E,
    handler: (payload: ServerEventPayload<E>) => void,
  ): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.handlers.get(event)!.add(handler as any);
  }

  /**
   * Remove a listener for a server event.
   */
  off<E extends ServerEventName>(event: E, handler?: (payload: ServerEventPayload<E>) => void): void {
    if (handler) {
      const set = this.handlers.get(event);
      if (set) {
        set.delete(handler as (...args: unknown[]) => void);
        if (set.size === 0) {
          this.handlers.delete(event);
        }
      }
    } else {
      this.handlers.delete(event);
    }
  }

  /**
   * Emit a client event to the server.
   */
  emit<E extends ClientEventName>(event: E, ...args: Parameters<ClientEvents[E]>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`[socket-manager] Cannot emit "${event as string}": not connected`);
      return;
    }
    const message = JSON.stringify({
      type: event,
      payload: args[0],
    });
    this.ws.send(message);
  }

  /**
   * Get the current connection ID, or null if not connected.
   */
  getSocketId(): string | null {
    return this.connectionId;
  }

  /**
   * Check if the WebSocket is currently connected.
   */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ---- Reconnection ----

  /**
   * Schedule reconnection with exponential backoff and jitter.
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();

    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts), maxDelay);

    // Add jitter: ±50% randomness to avoid thundering herd
    const jitter = delay * 0.5 * (Math.random() * 2 - 1);
    const finalDelay = delay + jitter;

    console.log(
      `[socket-manager] reconnecting in ${Math.round(finalDelay)}ms (attempt ${this.reconnectAttempts + 1})`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(`[socket-manager] reconnecting (attempt ${this.reconnectAttempts})...`);
      this.connect(this.serverUrl);
    }, finalDelay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ---- Heartbeat ----

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.lastPongReceived = Date.now();

    // Send ping every 25 seconds
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }

      // Check if pong was received within the last 35 seconds (25s interval + 10s grace)
      if (Date.now() - this.lastPongReceived > 35000) {
        console.warn('[socket-manager] Heartbeat timeout — no pong received');
        this.stopHeartbeat();
        if (this.ws) {
          this.ws.close(4001, 'Heartbeat timeout');
          this.ws = null;
        }
        this.connectionId = null;
        this.connectionStateHandler?.(false);
        this.scheduleReconnect();
      }
    }, 25000);

    // Set initial pong timeout
    this.resetPongTimeout();
  }

  private stopHeartbeat(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout !== null) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private resetPongTimeout(): void {
    if (this.pongTimeout !== null) {
      clearTimeout(this.pongTimeout);
    }
    // Set a backup timeout for 35 seconds
    this.pongTimeout = setTimeout(() => {
      // This is a fallback; the main check is in pingInterval
      if (Date.now() - this.lastPongReceived > 35000) {
        console.warn('[socket-manager] Pong timeout — no activity received');
        this.stopHeartbeat();
        if (this.ws) {
          this.ws.close(4001, 'Heartbeat timeout');
          this.ws = null;
        }
        this.connectionId = null;
        this.connectionStateHandler?.(false);
        this.scheduleReconnect();
      }
    }, 35000);
  }

  // ---- URL Resolution ----

  /**
   * Resolve the base server URL (without path) for WebSocket connections.
   * Uses VITE_SERVER_URL env var in production, or falls back to localhost.
   */
  private resolveBaseUrl(): string {
    // Vite statically replaces import.meta.env.VITE_SERVER_URL at build time
    const envUrl: string | undefined = import.meta.env.VITE_SERVER_URL;

    if (envUrl) {
      // Convert HTTP(S) URL to WebSocket(S) URL: http://... → ws://... , https://... → wss://...
      return String(envUrl).replace(/^http(s?):\/\//, 'ws$1://');
    }

    // In dev: use the current origin converted to WS, or fallback to localhost
    const origin = typeof window !== 'undefined'
      ? window.location.origin.replace(/^http(s?):\/\//, 'ws$1://')
      : 'ws://localhost:5173';
    return origin;
  }
}

/** Singleton instance for use across the client app. */
export const socketManager = new SocketManager();
